"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { motion } from "framer-motion";

const features = [
  {
    id: "01",
    title: "Dedicated Account Manager",
    desc: "A single point of contact with full visibility of your infrastructure. Direct communication, not ticket queues. Your account manager understands your volume targets and operational requirements.",
  },
  {
    id: "02",
    title: "Private Priority Support",
    desc: "A private support channel separate from the standard network. Priority response times and direct escalation access. When volume is high, your issues move to the front of the queue.",
  },
  {
    id: "03",
    title: "Volume-Based Rate Adjustments",
    desc: "As your monthly deployment volume increases, your rate structure improves. KPI-aligned pricing ensures our economics are tied to your performance outcomes, not arbitrary billing cycles.",
  },
  {
    id: "04",
    title: "Flexible Capital Scaling",
    desc: "Infrastructure capacity that adjusts to your client portfolio growth. Scale up or down based on active client demand without renegotiating terms or restructuring agreements.",
  },
  {
    id: "05",
    title: "Zero Scaled-Out Branding",
    desc: "Complete white-label operation. Your clients see your brand. Your infrastructure. We operate as the invisible layer. No Scaled-Out references in any client-facing communication or documentation.",
  },
];

export default function AgencyFeatures() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref?.current?.querySelectorAll(".reveal")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="py-24 bg-[#11161D]"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 reveal">
          <div className="tag-badge mx-auto mb-6 inline-flex">Agency Tier Features</div>
          <h2 className="section-title text-[#F0F4F8]">
            Built For <span className="text-[#00E676]">Agencies.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {features?.map((f, i) => (
            <div
              key={f?.id}
              className={`card-neon rounded-2xl p-6 reveal reveal-delay-${Math.min(i + 1, 5)} ${
                i === 4 ? "md:col-span-2 lg:col-span-1" : ""
              }`}
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="font-mono text-[#00E676] text-xs font-bold opacity-50">
                  {f?.id}
                </span>
                <div className="h-px flex-1 bg-[rgba(0,230,118,0.1)]" />
              </div>
              <h3 className="text-[#F0F4F8] font-bold text-base mb-3">{f?.title}</h3>
              <p className="text-[#6B7A8D] text-sm leading-relaxed">{f?.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA Block */}
        <div
          className="reveal reveal-delay-3 card-neon rounded-2xl p-10 md:p-14 text-center relative overflow-hidden"
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 60% 80% at 50% 100%, rgba(0,230,118,0.04) 0%, transparent 70%)",
            }}
          />
          <div className="relative z-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#F0F4F8] mb-4">
              Apply For <span className="text-[#00E676]">Agency Tier →</span>
            </h2>
            <p className="text-[#6B7A8D] text-sm max-w-lg mx-auto mb-8 leading-relaxed">
              Agency tier applications are reviewed individually. Minimum
              monthly volume thresholds apply. Approval is based on portfolio
              fit and operational alignment.
            </p>
            <Link
              href="/contact"
              className="btn-solid-glow px-10 py-4 rounded-full text-sm font-bold uppercase tracking-[0.1em] inline-block"
            >
              Apply For Agency Tier →
            </Link>
          </div>
        </div>
      </div>
    </motion.section>
  );
}